import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ESectionTypes, getSectionNameFromRoute } from '../../../shared/models/eop_types.enum';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { FormsModule } from '@angular/forms';
import { IAfterSchoolActivity } from '../../../shared/models/afterschoolActivity';
import { ActivityDetailsComponent } from './activity-details/activity-details.component';
import { EditActivityComponent } from './edit-activity/edit-activity.component';
import { NewActivityComponent } from './new-activity/new-activity.component';
import { AfterschoolActivityService } from '../../../shared/services/afterschoolActivity.service';
import { ContactsService } from '../../../shared/services/contact.service';

// The MS Athletic Zone Plan captures only the event name and courses of action; the AL
// After School Activities page collects the full detail set (location, contacts, equipment).
const NAME_AND_ACTIONS_ONLY_ROUTES = ['athletic-zone-plan'];

@Component({
  selector: 'afterschool-activities',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    IntroductionComponent,
    SpinnerComponent,
    PopupMessageComponent,
    PrvNextButtonsComponent,
    DeleteConfirmationComponent,
    NgxPaginationModule,
    ActivityDetailsComponent,
    EditActivityComponent,
    NewActivityComponent
],
  templateUrl: './afterschool-activities.component.html',
  styleUrl: './afterschool-activities.component.css'
})
export class AfterschoolActivitiesComponent implements OnInit {
  eopSectionTypeId: ESectionTypes = ESectionTypes["After School Activities"];
  displayTitle: string = 'After School Activities';
  showFullDetails: boolean = true;

  loading: boolean = false;
  sortOrder: boolean = true;
  showThreatObjectivesDetail: boolean = false;
  showEditThreatObjectives: boolean = false;
  showCreateNewThreatObjectives: boolean = false;
  showDeleteConfirmation: boolean = false;

  icons = Icons;
  searchQuery: string = '';
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  fullAfterSchoolActivities: IAfterSchoolActivity[] = [];
  afterSchoolActivitiesList: IAfterSchoolActivity[] = [];
  showModalType: string = '';
  messageModal: string | null = null;
  popupMessage: string = '';
  selectedActivity: IAfterSchoolActivity | null = null;
  page = 1;
  itemsPerPage = 10;
    fullContactsList: IContact[] = [];

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private afterschoolActivityService: AfterschoolActivityService
  ) {}

  ngOnInit(): void {
    this.applyRouteContext();
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
  }

  private applyRouteContext(): void {
    const segments = this.router.url.replace(/\/+$/, '').split('/');
    const lastSegment = segments[segments.length - 1] ?? '';
    this.showFullDetails = !NAME_AND_ACTIONS_ONLY_ROUTES.includes(lastSegment);

    const sectionName = getSectionNameFromRoute(lastSegment);
    if (sectionName) {
      this.displayTitle = sectionName;
      this.eopSectionTypeId = ESectionTypes[sectionName as keyof typeof ESectionTypes];
    }
  }

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        console.log('Contacts loaded successfully:', response);
        this.loadAfterschoolActivities();
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  loadAfterschoolActivities(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.afterschoolActivityService
        .getBuildingAfterschoolActivity(this.currentBuildingId)
        .subscribe({
          next: (res) => {
            console.log('Afterschool Activities loaded successfully:', res);
            if (!res.has_error) {
              this.fullAfterSchoolActivities = res.data;
  
              this.afterSchoolActivitiesList = this.fullAfterSchoolActivities.map((activity: any) => {
                const primaryContact = this.fullContactsList.find(c => c.id === activity.primary_contact_id);
                const secondaryContact = this.fullContactsList.find(c => c.id === activity.secondary_contact_id);
  
                // ✅ helper to resolve array of contact IDs → comma-separated names
                const getNames = (ids?: number[]): string => {
                  return (ids || [])
                    .map((id: number) => this.fullContactsList.find(c => c.id === id)?.name)
                    .filter((name): name is string => !!name)
                    .join(', ') || 'N/A';
                };
  
                return {
                  ...activity,
                  primary_contact_name: primaryContact ? primaryContact.name : 'N/A',
                  secondary_contact_name: secondaryContact ? secondaryContact.name : 'N/A',
                  first_aid_contact_names: getNames(activity.first_aid_contact_ids),
                  cpr_contact_names: getNames(activity.cpr_contact_ids),
                  aed_contact_names: getNames(activity.aed_contact_ids),
                  incident_reporting_contact_names: getNames(activity.incident_reporting_contact_ids),
                  ieap_contact_names: getNames(activity.ieap_contact_ids),
                  emergency_diabetic_contact_names: getNames(activity.emergency_diabetic_contact_ids),
                };
              });
            }
            this.loading = false;
          },
          error: (err) => {
            this.loading = false;
            this.messageModal = 'unsuccess';
            this.popupMessage =
              err.message ||
              'Failed to load afterschool activities. Please try again.';
          },
        });
    }
  }
  

  // sort threat-goal added to header of table
  sortList(key: keyof typeof this.afterSchoolActivitiesList[0]): void {
    this.sortOrder = !this.sortOrder;
    this.afterSchoolActivitiesList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    this.showModalType = modalName;
    this.selectedActivity = selectedItem ? selectedItem : null;
  }

  handleCloseModal(modalName: string): void {
    this.showModalType = '';
    this.selectedActivity = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadAfterschoolActivities();

    if (message.type === 'success') {
      this.messageModal = 'success';
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
    } 
    this.popupMessage = message.message;
  }  

  approveAfterschoolActivities(): void {
    if (this.currentBuildingId) {
      this.afterschoolActivityService.approveBuildingAfterschoolActivity(this.currentBuildingId).subscribe({
        next: (res) => {
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = `This occurred after confirming ${this.displayTitle}.`;
            this.loadAfterschoolActivities();
          }
        },
        error: (err) => {
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  }

  handleDelete(item: any): void {
    this.loading = true;
    if (this.currentBuildingId && item.id) {
      this.afterschoolActivityService.deleteAfterschoolActivity(this.currentBuildingId, item.id.toString()).subscribe({
        next: (res) => {
          this.loading = false;
          this.showModalType = '';
          if (!res.has_error) {
            this.messageModal = 'success';
            this.popupMessage = `This occurred after deleting an entry from ${this.displayTitle}.`;
            this.loadAfterschoolActivities();
          }
        },
        error: (err) => {
          this.loading = false;
          this.showModalType = '';
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      })
    }
  } 

  filterResourcesList(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.afterSchoolActivitiesList = this.fullAfterSchoolActivities.filter((item) => {
      return (
        item.activity_name.toLowerCase().includes(query) ||
        item.location.toLowerCase().includes(query) ||
        item.last_updated_user_name?.toLowerCase().includes(query) ||
        item.last_updated_at?.toLowerCase().includes(query) ||
        item.primary_contact_name?.toLowerCase().includes(query) ||
        item.secondary_contact_name?.toLowerCase().includes(query)
      );
    });
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }

}
